import React from "react";
import { ThemeProvider } from "next-themes";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {Navigate, Route, BrowserRouter, Routes} from "react-router";
import DashboardPage from "./pages/DashboardPage";
import LoginPage from "./pages/LoginPage";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import AdminConsentPage from "./pages/AdminConsentPage";
import AdminConsentResultPage from "./pages/AdminConsentResultPage";
import McpAuthorizePage from "./pages/McpAuthorizePage";
import {NAVIGATION_KEYS} from "./constants/NavigationKeys";
import { ThemeVariantProvider } from "./contexts/ThemeVariantContext";
import ErrorBoundary from "./components/common/ErrorBoundary";

const queryClient = new QueryClient();

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider attribute="class" defaultTheme="system" enableSystem disableTransitionOnChange>
        <ThemeVariantProvider>
          <div className="flex h-dvh">
            <ErrorBoundary>
            <Routes>
              <Route
                path="/adminconsent" 
                element={<AdminConsentPage />} 
              />
              <Route
                path="/adminconsent/result"
                element={<AdminConsentResultPage />}
              />
              <Route
                path="/login"
                element={<LoginPage />}
              />
              <Route
                path="/oauth/authorize"
                element={<McpAuthorizePage />}
              />
              <Route
                path="/change-password"
                element={<ChangePasswordPage />}
              />
              <Route 
                path="/" 
                element={<Navigate to="/organizations" />}
              />
              <Route
                path="/organizations"
                element={<DashboardPage selectedNavigation={NAVIGATION_KEYS.ORGANIZATIONS} />}
              />
              <Route
                path="/users"
                element={<DashboardPage selectedNavigation={NAVIGATION_KEYS.USERS} />}
              />
              <Route
                path="/versions"
                element={<DashboardPage selectedNavigation={NAVIGATION_KEYS.VERSIONS} />}
              />
              <Route
                path="/jobs"
                element={<DashboardPage selectedNavigation={NAVIGATION_KEYS.JOBS} />}
              />
              <Route
                path="/organization/:organizationId"
                element={<DashboardPage selectedNavigation={NAVIGATION_KEYS.OVERVIEW} />}
              />
              <Route
                path="/organization/:organizationId/audit-logs"
                element={<DashboardPage selectedNavigation={NAVIGATION_KEYS.AUDIT_LOGS} />}
              />
              <Route
                path="/organization/:organizationId/discovery-cycles"
                element={<DashboardPage selectedNavigation={NAVIGATION_KEYS.DISCOVERY_CYCLES} />}
              />
              <Route
                path="/organization/:organizationId/configuration"
                element={<DashboardPage selectedNavigation={NAVIGATION_KEYS.ORGANIZATION_CONFIGURATION} />}
              />
              <Route
                path="/organization/:organizationId/client-configurations"
                element={<DashboardPage selectedNavigation={NAVIGATION_KEYS.CLIENT_CONFIGURATIONS} />}
              />
              <Route
                path="/organization/:organizationId/client-configuration/create"
                element={<DashboardPage selectedNavigation={NAVIGATION_KEYS.CLIENT_CONFIGURATION} />}
              />
              <Route
                path="/organization/:organizationId/client-configuration/:configurationId"
                element={<DashboardPage selectedNavigation={NAVIGATION_KEYS.CLIENT_CONFIGURATION} />}
              />
              <Route
                path="/organization/:organizationId/clients"
                element={<DashboardPage selectedNavigation={NAVIGATION_KEYS.CLIENTS} />}
              />
              <Route
                path="/organization/:organizationId/cases"
                element={<DashboardPage selectedNavigation={NAVIGATION_KEYS.CASES} />}
              />
              <Route
                path="/organization/:organizationId/case/:caseId"
                element={<DashboardPage selectedNavigation={NAVIGATION_KEYS.CASE} />}
              />
              <Route
                path="/organization/:organizationId/case-files"
                element={<DashboardPage selectedNavigation={NAVIGATION_KEYS.CASE_FILES} />}
              />
              <Route
                path="/organization/:organizationId/intakes"
                element={<DashboardPage selectedNavigation={NAVIGATION_KEYS.INTAKES} />}
              />
              <Route
                path="/organization/:organizationId/intake/:intakeId"
                element={<DashboardPage selectedNavigation={NAVIGATION_KEYS.INTAKE} />}
              />
              <Route
                path="/organization/:organizationId/intake-files"
                element={<DashboardPage selectedNavigation={NAVIGATION_KEYS.INTAKE_FILES} />}
              />
              <Route
                path="/organization/:organizationId/firm-documents-folders"
                element={<DashboardPage selectedNavigation={NAVIGATION_KEYS.FIRM_DOCUMENTS_FOLDERS} />}
              />
              <Route
                path="/organization/:organizationId/firm-documents-folder/:firmDocumentsFolderId"
                element={<DashboardPage selectedNavigation={NAVIGATION_KEYS.FIRM_DOCUMENTS_FOLDER} />}
              />
              <Route
                path="/organization/:organizationId/firm-documents"
                element={<DashboardPage selectedNavigation={NAVIGATION_KEYS.FIRM_DOCUMENTS} />}
              />
              <Route
                path="/organization/:organizationId/providers"
                element={<DashboardPage selectedNavigation={NAVIGATION_KEYS.PROVIDERS} />}
              />
              <Route
                path="/organization/:organizationId/provider/:providerId"
                element={<DashboardPage selectedNavigation={NAVIGATION_KEYS.PROVIDER} />}
              />
              <Route
                path="/organization/:organizationId/provider-files"
                element={<DashboardPage selectedNavigation={NAVIGATION_KEYS.PROVIDER_FILES} />}
              />
              <Route
                path="/organization/:organizationId/templates"
                element={<DashboardPage selectedNavigation={NAVIGATION_KEYS.TEMPLATES} />}
              />
              <Route
                path="/organization/:organizationId/template/:templateId"
                element={<DashboardPage selectedNavigation={NAVIGATION_KEYS.TEMPLATE} />}
              />
              <Route
                path="/organization/:organizationId/template-files"
                element={<DashboardPage selectedNavigation={NAVIGATION_KEYS.TEMPLATE_FILES} />}
              />
              <Route
                path="/organization/:organizationId/flagged-paths"
                element={<DashboardPage selectedNavigation={NAVIGATION_KEYS.FLAGGED_PATHS} />}
              />
              <Route
                path="/organization/:organizationId/renamed-folders"
                element={<DashboardPage selectedNavigation={NAVIGATION_KEYS.RENAMED_FOLDERS} />}
              />
              <Route
                path="/organization/:organizationId/source-links"
                element={<DashboardPage selectedNavigation={NAVIGATION_KEYS.SOURCE_LINKS} />}
              />
              <Route
                path="/organization/:organizationId/execute-sql-scripts"
                element={<DashboardPage selectedNavigation={NAVIGATION_KEYS.EXECUTE_SQL_SCRIPTS} />}
              />
              <Route
                path="/organization/:organizationId/recalculate-file-priorities"
                element={<DashboardPage selectedNavigation={NAVIGATION_KEYS.RECALCULATE_FILE_PRIORITIES} />}
              />
              <Route
                path="/organization/:organizationId/bulk-delete"
                element={<DashboardPage selectedNavigation={NAVIGATION_KEYS.BULK_DELETE} />}
              />
              <Route
                path="/organization/:organizationId/resolve-conflicts-by-rule"
                element={<DashboardPage selectedNavigation={NAVIGATION_KEYS.RESOLVE_CONFLICTS_BY_RULE} />}
              />
              <Route
                path="/docs/*"
                element={<DashboardPage selectedNavigation={NAVIGATION_KEYS.DOCS} />}
              />
              <Route
                path="/organization/:organizationId/docs/*"
                element={<DashboardPage selectedNavigation={NAVIGATION_KEYS.DOCS} />}
              />
            </Routes>
            </ErrorBoundary>
          </div>
        </ThemeVariantProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
};

export default App;
